import { z } from 'zod';

export const McpTransportSchema = z.enum(['SSE', 'HTTP', 'STDIO']);
export const McpScopeSchema = z.enum(['USER', 'TENANT']);
export const McpCredentialTypeSchema = z.enum([
  'BEARER',
  'API_KEY',
  'BASIC',
  'OAUTH',
  'OAUTH_DCR',
  'NONE',
]);
export const McpCredentialStatusSchema = z.enum([
  'VALID',
  'EXPIRED',
  'REVOKED',
]);
export const McpTargetTypeSchema = z.enum([
  'COPILOT',
  'APPLICATION',
  'EXTERNAL_NETWORK',
]);

export type McpTransportType = z.infer<typeof McpTransportSchema>;
export type McpScopeType = z.infer<typeof McpScopeSchema>;
export type McpCredentialTypeType = z.infer<typeof McpCredentialTypeSchema>;
export type McpCredentialStatusType = z.infer<typeof McpCredentialStatusSchema>;
export type McpTargetTypeType = z.infer<typeof McpTargetTypeSchema>;

export const McpServerResponseSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  transport: McpTransportSchema,
  url: z.string().nullable(),
  command: z.string().nullable(),
  args: z.array(z.string()),
  scope: McpScopeSchema,
  userId: z.string().nullable(),
  enabled: z.boolean(),
  requiresApproval: z.boolean(),
  allowedTargetTypes: z.array(McpTargetTypeSchema),
  credentialType: McpCredentialTypeSchema.nullable(),
  credentialStatus: McpCredentialStatusSchema.nullable(),
  hasOauthConfig: z.boolean(),
  consecutiveFailures: z.number(),
  lastHealthCheck: z.iso.datetime().nullable(),
  lastError: z.string().nullable(),
  lastErrorAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type McpServerResponseDto = z.infer<typeof McpServerResponseSchema>;

export const McpServerListResponseSchema = z.object({
  servers: z.array(McpServerResponseSchema),
});

export const CreateMcpServerSchema = z
  .object({
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    transport: McpTransportSchema,
    url: z.url().optional(),
    command: z.string().min(1).optional(),
    args: z.array(z.string()).default([]),
    scope: McpScopeSchema,
    requiresApproval: z.boolean().default(true),
    allowedTargetTypes: z.array(McpTargetTypeSchema).default(['COPILOT']),
    discoveryUrl: z.url().optional(),
  })
  .refine(
    (data) => {
      if (data.transport === 'STDIO') return !!data.command;
      return !!data.url;
    },
    {
      error:
        'URL is required for HTTP and SSE transport; command is required for STDIO transport',
    },
  );

export type CreateMcpServerDto = z.infer<typeof CreateMcpServerSchema>;

export const UpdateMcpServerSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  transport: McpTransportSchema.optional(),
  url: z.url().nullable().optional(),
  command: z.string().min(1).nullable().optional(),
  args: z.array(z.string()).optional(),
  enabled: z.boolean().optional(),
  requiresApproval: z.boolean().optional(),
  allowedTargetTypes: z.array(McpTargetTypeSchema).optional(),
});

export type UpdateMcpServerDto = z.infer<typeof UpdateMcpServerSchema>;

const McpCredentialLabelSchema = z.string().min(1).max(100).optional();

export const UpsertMcpCredentialSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('NONE'), label: McpCredentialLabelSchema }),
  z.object({
    type: z.literal('BEARER'),
    token: z.string().min(1),
    label: McpCredentialLabelSchema,
  }),
  z.object({
    type: z.literal('API_KEY'),
    header: z.string().min(1),
    key: z.string().min(1),
    label: McpCredentialLabelSchema,
  }),
  z.object({
    type: z.literal('BASIC'),
    username: z.string().min(1),
    password: z.string().min(1),
    label: McpCredentialLabelSchema,
  }),
  z.object({
    type: z.literal('OAUTH'),
    authorizeUrl: z.url(),
    tokenUrl: z.url(),
    clientId: z.string().min(1),
    clientSecret: z.string().optional(),
    scopes: z.array(z.string()).default([]),
    label: McpCredentialLabelSchema,
  }),
  z.object({
    type: z.literal('OAUTH_DCR'),
    discoveryUrl: z.url(),
    scopes: z.array(z.string()).default([]),
    label: McpCredentialLabelSchema,
  }),
]);

export type UpsertMcpCredentialDto = z.infer<typeof UpsertMcpCredentialSchema>;

export const McpCredentialResponseSchema = z.object({
  type: McpCredentialTypeSchema,
  status: McpCredentialStatusSchema,
});

export type McpCredentialResponseDto = z.infer<
  typeof McpCredentialResponseSchema
>;

export const McpServerCapabilitiesSchema = z.looseObject({
  tools: z.record(z.string(), z.unknown()).optional(),
  prompts: z.record(z.string(), z.unknown()).optional(),
  resources: z.record(z.string(), z.unknown()).optional(),
  logging: z.record(z.string(), z.unknown()).optional(),
  completions: z.record(z.string(), z.unknown()).optional(),
  experimental: z.record(z.string(), z.unknown()).optional(),
});

export type McpServerCapabilitiesDto = z.infer<
  typeof McpServerCapabilitiesSchema
>;

export const McpServerInfoSchema = z.object({
  name: z.string(),
  version: z.string(),
});

export type McpServerInfoDto = z.infer<typeof McpServerInfoSchema>;

export const McpProbeStatusSchema = z.enum([
  'ok',
  'empty',
  'not_found',
  'error',
]);

export type McpProbeStatusDto = z.infer<typeof McpProbeStatusSchema>;

export const McpProbeSummaryEntrySchema = z.object({
  method: z.string(),
  status: McpProbeStatusSchema,
});

export type McpProbeSummaryEntryDto = z.infer<
  typeof McpProbeSummaryEntrySchema
>;

export const McpCapabilityMismatchKindSchema = z.enum([
  'declared_no_surface',
  'surface_no_declared',
]);

export type McpCapabilityMismatchKindDto = z.infer<
  typeof McpCapabilityMismatchKindSchema
>;

export const McpCapabilityMismatchSchema = z.object({
  capability: z.string(),
  kind: McpCapabilityMismatchKindSchema,
});

export type McpCapabilityMismatchDto = z.infer<
  typeof McpCapabilityMismatchSchema
>;

export const McpDiscoveredToolSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  inputSchema: z.record(z.string(), z.unknown()).nullable(),
  outputSchema: z.record(z.string(), z.unknown()).nullable(),
  annotations: z.record(z.string(), z.unknown()).nullable(),
  meta: z.record(z.string(), z.unknown()).nullable().optional(),
  definitionHash: z.string(),
});

export type McpDiscoveredToolDto = z.infer<typeof McpDiscoveredToolSchema>;

export const McpSnapshotIdentityAccessEntrySchema = z.object({
  identityId: z.string(),
  label: z.string(),
  visible: z.boolean(),
  missingScopes: z.array(z.string()).optional(),
  callOutcome: z
    .enum(['success', 'denied', 'insufficient_scope', 'error', 'untested'])
    .optional(),
});

export type McpSnapshotIdentityAccessEntryDto = z.infer<
  typeof McpSnapshotIdentityAccessEntrySchema
>;

export const McpSnapshotAssetAccessSchema = z.object({
  identityAccess: z.array(McpSnapshotIdentityAccessEntrySchema).optional(),
  accessScope: z.enum(['ALL', 'SOME']).optional(),
});

export type McpSnapshotAssetAccessDto = z.infer<
  typeof McpSnapshotAssetAccessSchema
>;

export const NormalizedMcpCapabilitySnapshotSchema = z.object({
  tools: z.array(
    z
      .object({
        name: z.string(),
        description: z.string().nullable(),
        inputSchema: z.record(z.string(), z.unknown()).nullable(),
        annotations: z.record(z.string(), z.unknown()).nullable(),
      })
      .merge(McpSnapshotAssetAccessSchema),
  ),
  prompts: z.array(
    z
      .object({
        name: z.string(),
        description: z.string().nullable(),
        templateBody: z.string().nullable(),
      })
      .merge(McpSnapshotAssetAccessSchema),
  ),
  resources: z.array(
    z
      .object({
        uri: z.string(),
        name: z.string().nullable(),
        mimeType: z.string().nullable(),
        sampleContent: z.string().nullable(),
      })
      .merge(McpSnapshotAssetAccessSchema),
  ),
});

export type NormalizedMcpCapabilitySnapshot = z.infer<
  typeof NormalizedMcpCapabilitySnapshotSchema
>;

export const McpDiscoveredPromptArgumentSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  required: z.boolean().optional(),
});

export type McpDiscoveredPromptArgumentDto = z.infer<
  typeof McpDiscoveredPromptArgumentSchema
>;

export const McpDiscoveredPromptSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  arguments: z.array(McpDiscoveredPromptArgumentSchema).optional().default([]),
  templateBody: z.string().nullable().optional(),
});

export type McpDiscoveredPromptDto = z.infer<typeof McpDiscoveredPromptSchema>;

export const McpDiscoveredResourceSchema = z.object({
  uri: z.string(),
  name: z.string().nullable(),
  mimeType: z.string().nullable(),
  sampleContent: z.string().nullable().optional(),
});

export type McpDiscoveredResourceDto = z.infer<
  typeof McpDiscoveredResourceSchema
>;

export const McpResourceTemplateSchema = z.object({
  uriTemplate: z.string(),
  name: z.string().nullable(),
  mimeType: z.string().nullable(),
});

export type McpResourceTemplateDto = z.infer<typeof McpResourceTemplateSchema>;

export const McpTestResultSchema = z.object({
  success: z.boolean(),
  error: z.string().nullable(),
  protocolVersion: z.string().nullable(),
  serverInfo: McpServerInfoSchema.nullable(),
  capabilities: McpServerCapabilitiesSchema.nullable(),
  supportedMethods: z.array(z.string()),
  probeSummary: z.array(McpProbeSummaryEntrySchema),
  capabilityMismatches: z.array(McpCapabilityMismatchSchema),
  tools: z.array(McpDiscoveredToolSchema),
  resourceCount: z.number(),
  resources: z.array(McpDiscoveredResourceSchema).optional().default([]),
  resourceTemplates: z.array(McpResourceTemplateSchema).optional().default([]),
  promptCount: z.number(),
  prompts: z.array(McpDiscoveredPromptSchema),
  completionSuggestions: z
    .record(z.string(), z.array(z.string()))
    .optional()
    .default({}),
  latencyMs: z.number(),
  httpStatus: z.number().optional(),
  authChallenge: z
    .object({
      error: z.string().optional(),
      scopes: z.array(z.string()),
      resourceMetadataUrl: z.string().optional(),
    })
    .optional(),
});

export type McpTestResultDto = z.infer<typeof McpTestResultSchema>;

export const McpServerIdentitySchema = z.object({
  id: z.string(),
  label: z.string(),
  isDefault: z.boolean(),
  type: McpCredentialTypeSchema,
  status: McpCredentialStatusSchema,
  hasOauthConfig: z.boolean(),
  scopes: z.array(z.string()).optional(),
  oauthScopesSupported: z.array(z.string()).optional(),
  requestedOAuthScopes: z.array(z.string()).optional(),
  isAnonymous: z.boolean().optional(),
});

export type McpServerIdentityDto = z.infer<typeof McpServerIdentitySchema>;

export const McpServerIdentityListResponseSchema = z.object({
  identities: z.array(McpServerIdentitySchema),
});

export const CreateMcpIdentitySchema = z
  .object({
    label: z.string().min(1).max(100),
    isDefault: z.boolean().optional(),
    scopes: z.array(z.string()).optional(),
  })
  .and(UpsertMcpCredentialSchema);

export type CreateMcpIdentityDto = z.infer<typeof CreateMcpIdentitySchema>;

export const UpdateMcpIdentitySchema = z.object({
  label: z.string().min(1).max(100).optional(),
  isDefault: z.boolean().optional(),
  scopes: z.array(z.string()).optional(),
  credential: UpsertMcpCredentialSchema.optional(),
});

export type UpdateMcpIdentityDto = z.infer<typeof UpdateMcpIdentitySchema>;

export const McpIdentityAuthStatusSchema = z.enum([
  'authorized',
  'auth_required',
  'insufficient_scope',
  'unknown',
]);

export type McpIdentityAuthStatusDto = z.infer<
  typeof McpIdentityAuthStatusSchema
>;

export const McpIdentityConnectionStatusSchema = z.object({
  id: z.string(),
  label: z.string(),
  connectionSuccess: z.boolean(),
  error: z.string().nullable(),
  scopes: z.array(z.string()).optional(),
  isAnonymous: z.boolean().optional(),
  authStatus: McpIdentityAuthStatusSchema.optional(),
  challengeScopes: z.array(z.string()).optional(),
});

export type McpIdentityConnectionStatusDto = z.infer<
  typeof McpIdentityConnectionStatusSchema
>;

export const McpDiscoveryAuthModelSchema = z.object({
  scopesSupported: z.array(z.string()),
  authorizationServers: z.array(z.string()),
  requiresAuth: z.boolean(),
});

export type McpDiscoveryAuthModelDto = z.infer<
  typeof McpDiscoveryAuthModelSchema
>;

export const DiscoverMcpOAuthScopesQuerySchema = z.object({
  discoveryUrl: z.string().url(),
});

export type DiscoverMcpOAuthScopesQueryDto = z.infer<
  typeof DiscoverMcpOAuthScopesQuerySchema
>;

export const DiscoverMcpServerOAuthScopesQuerySchema = z.object({
  identityId: z.string().optional(),
});

export type DiscoverMcpServerOAuthScopesQueryDto = z.infer<
  typeof DiscoverMcpServerOAuthScopesQuerySchema
>;

export const McpDiscoveredToolWithAccessSchema = McpDiscoveredToolSchema.extend(
  {
    accessibleIdentityIds: z.array(z.string()),
  },
);

export type McpDiscoveredToolWithAccessDto = z.infer<
  typeof McpDiscoveredToolWithAccessSchema
>;

export const McpDiscoveredPromptWithAccessSchema =
  McpDiscoveredPromptSchema.extend({
    accessibleIdentityIds: z.array(z.string()),
  });

export type McpDiscoveredPromptWithAccessDto = z.infer<
  typeof McpDiscoveredPromptWithAccessSchema
>;

export const McpDiscoveredResourceWithAccessSchema =
  McpDiscoveredResourceSchema.extend({
    accessibleIdentityIds: z.array(z.string()),
  });

export type McpDiscoveredResourceWithAccessDto = z.infer<
  typeof McpDiscoveredResourceWithAccessSchema
>;

export const McpMultiIdentityDiscoveryResultSchema = z.object({
  success: z.boolean(),
  error: z.string().nullable(),
  identities: z.array(McpIdentityConnectionStatusSchema),
  protocolVersion: z.string().nullable(),
  serverInfo: McpServerInfoSchema.nullable(),
  capabilities: McpServerCapabilitiesSchema.nullable(),
  supportedMethods: z.array(z.string()),
  probeSummary: z.array(McpProbeSummaryEntrySchema),
  capabilityMismatches: z.array(McpCapabilityMismatchSchema),
  tools: z.array(McpDiscoveredToolWithAccessSchema),
  resourceCount: z.number(),
  resources: z
    .array(McpDiscoveredResourceWithAccessSchema)
    .optional()
    .default([]),
  resourceTemplates: z.array(McpResourceTemplateSchema).optional().default([]),
  promptCount: z.number(),
  prompts: z.array(McpDiscoveredPromptWithAccessSchema),
  completionSuggestions: z
    .record(z.string(), z.array(z.string()))
    .optional()
    .default({}),
  latencyMs: z.number(),
  authModel: McpDiscoveryAuthModelSchema.nullable().optional(),
});

export type McpMultiIdentityDiscoveryResultDto = z.infer<
  typeof McpMultiIdentityDiscoveryResultSchema
>;

export const ApplicationMcpServerResponseSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  mcpServerId: z.string(),
  enabled: z.boolean(),
  server: McpServerResponseSchema,
});

export type ApplicationMcpServerResponseDto = z.infer<
  typeof ApplicationMcpServerResponseSchema
>;

export const ApplicationMcpServerListResponseSchema = z.object({
  servers: z.array(ApplicationMcpServerResponseSchema),
});

export const AttachMcpServerSchema = z.object({
  enabled: z.boolean().default(true),
});

export type AttachMcpServerDto = z.infer<typeof AttachMcpServerSchema>;

export const OAuthAuthorizeResponseSchema = z.object({
  authorizeUrl: z.url(),
});

export const OAuthAuthorizeQuerySchema = z.object({
  openerOrigin: z.string().optional(),
  gatewayOrigin: z
    .string()
    .optional()
    .describe(
      'Public gateway origin used for OAuth redirect_uri registration (e.g. https://api.staging.example.com).',
    ),
  desktopScheme: z.string().optional(),
  callbackPath: z.string().optional(),
});

export const GetMcpServersQuerySchema = z.object({
  scope: McpScopeSchema.optional(),
  applicationId: z.string().optional(),
});

export const McpCopilotAvailableServerSchema = McpServerResponseSchema.extend({
  optedIn: z.boolean(),
});

export type McpCopilotAvailableServerDto = z.infer<
  typeof McpCopilotAvailableServerSchema
>;

export const McpCopilotAvailableServersResponseSchema = z.object({
  servers: z.array(McpCopilotAvailableServerSchema),
});

export const McpAuthStateIdentitySchema = z.object({
  id: z.string(),
  label: z.string(),
  isDefault: z.boolean(),
  type: McpCredentialTypeSchema,
  status: McpCredentialStatusSchema,
  isAnonymous: z.boolean().optional(),
  scopes: z.array(z.string()).optional(),
  hasOauthConfig: z.boolean(),
  tokenExpiresAt: z.string().nullable(),
});

export type McpAuthStateIdentityDto = z.infer<
  typeof McpAuthStateIdentitySchema
>;

export const McpAuthProbeIdentitySchema = z.object({
  id: z.string(),
  label: z.string(),
  connectionSuccess: z.boolean(),
  authStatus: McpIdentityAuthStatusSchema.optional(),
  challengeScopes: z.array(z.string()).optional(),
  error: z.string().nullable(),
  accessibleToolCount: z.number(),
});

export type McpAuthProbeIdentityDto = z.infer<
  typeof McpAuthProbeIdentitySchema
>;

export const McpAuthProbeResultSchema = z.object({
  success: z.boolean(),
  error: z.string().nullable(),
  identities: z.array(McpAuthProbeIdentitySchema),
  authModel: McpDiscoveryAuthModelSchema.nullable().optional(),
});

export type McpAuthProbeResultDto = z.infer<typeof McpAuthProbeResultSchema>;

export const McpApplicationAuthStateSchema = z.object({
  linked: z.boolean(),
  mcpServerId: z.string().nullable(),
  mcpTransport: McpTransportSchema.nullable(),
  url: z.string().nullable(),
  identities: z.array(McpAuthStateIdentitySchema),
  authModel: McpDiscoveryAuthModelSchema.nullable().optional(),
});

export type McpApplicationAuthStateDto = z.infer<
  typeof McpApplicationAuthStateSchema
>;
